import { Ball, BallMode, SystemInfo } from '@deepwake/shared';
import { ClientSim, BallDeco } from './sim.js';

const MODE_NAMES: Record<number, string> = {
  [BallMode.GOTO]: 'En route',
  [BallMode.FOLLOW]: 'Keeping range',
  [BallMode.STOP]: 'Stopped',
  [BallMode.WARP]: 'Warping',
  [BallMode.ORBIT]: 'Orbiting',
  [BallMode.MISSILE]: 'Tracking',
  [BallMode.FORMATION]: 'In formation',
};

export function fmtDist(m: number): string {
  if (m < 1000) return `${m.toFixed(0)} m`;
  if (m < 1e6) return `${(m / 1000).toFixed(1)} km`;
  if (m < 0.05 * 0.1495978707e12) return `${(m / 1e6).toFixed(1)} Mm`;
  return `${(m / 0.1495978707e12).toFixed(2)} AU`;
}

export interface HudCallbacks {
  onCommand: (cmd: string, targetId: number | null, arg?: number) => void;
  onChat: (text: string) => void;
  onModule: (slot: number, active: boolean) => void;
  onTune: (value: number) => void;
}

export class Hud {
  root: HTMLElement;
  selected: number | null = null;
  private cb: HudCallbacks;
  private overviewEl: HTMLElement;
  private selPanelEl: HTMLElement;
  private shipEl: HTMLElement;
  private sysEl: HTMLElement;
  private chatLog: HTMLElement;
  private toastEl: HTMLElement;
  private combatEl: HTMLElement;
  lastStatus: import('@deepwake/shared').ServerMessage & { t: 'status' } | null = null;

  constructor(root: HTMLElement, cb: HudCallbacks) {
    this.root = root;
    this.cb = cb;
    root.innerHTML = `
      <div id="sys" class="panel"></div>
      <div id="ship" class="panel"></div>
      <div id="overview" class="panel"><div class="ptitle">Overview</div><div id="ovlist"></div></div>
      <div id="selpanel" class="panel hidden"></div>
      <div id="chat" class="panel">
        <div id="chatlog"></div>
        <input id="chatin" placeholder="Local comms — Enter to send" maxlength="300"/>
      </div>
      <div id="combat" class="panel hidden"></div>
      <div id="toast"></div>
    `;
    this.sysEl = root.querySelector('#sys')!;
    this.shipEl = root.querySelector('#ship')!;
    this.overviewEl = root.querySelector('#ovlist')!;
    this.selPanelEl = root.querySelector('#selpanel')!;
    this.chatLog = root.querySelector('#chatlog')!;
    this.toastEl = root.querySelector('#toast')!;
    this.combatEl = root.querySelector('#combat')!;

    const input = root.querySelector<HTMLInputElement>('#chatin')!;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && input.value.trim()) {
        cb.onChat(input.value.trim());
        input.value = '';
      }
      e.stopPropagation();
    });
  }

  toast(text: string): void {
    this.toastEl.textContent = text;
    this.toastEl.classList.add('show');
    setTimeout(() => this.toastEl.classList.remove('show'), 2600);
  }

  chat(from: string, text: string): void {
    const div = document.createElement('div');
    div.innerHTML = `<b></b> <span></span>`;
    (div.querySelector('b') as HTMLElement).textContent = from;
    (div.querySelector('span') as HTMLElement).textContent = text;
    this.chatLog.appendChild(div);
    while (this.chatLog.children.length > 60) this.chatLog.firstChild?.remove();
    this.chatLog.scrollTop = this.chatLog.scrollHeight;
  }

  setSystem(info: SystemInfo): void {
    const secColor = info.security === 'core' ? '#6fd47b' : info.security === 'verge' ? '#e8c35a' : '#e06a5a';
    this.sysEl.innerHTML = `<div class="ptitle"></div><div class="sub"><span style="color:${secColor}"></span> · </div>`;
    (this.sysEl.querySelector('.ptitle') as HTMLElement).textContent = info.name;
    (this.sysEl.querySelector('.sub span') as HTMLElement).textContent = info.security.toUpperCase();
    this.sysEl.querySelector('.sub')!.append(info.region);
  }

  update(sim: ClientSim, docked: number | null): void {
    const me = sim.myBall();

    // Own ship panel
    if (docked !== null) {
      const st = sim.deco.get(docked);
      this.shipEl.innerHTML = `<div class="ptitle">Docked</div><div class="sub"></div><button data-cmd="undock">Undock</button>`;
      (this.shipEl.querySelector('.sub') as HTMLElement).textContent = st?.name ?? 'Station';
      this.bindButtons(this.shipEl);
    } else if (me) {
      const speed = me.newVel.length();
      this.shipEl.innerHTML = `
        <div class="ptitle">${MODE_NAMES[me.mode] ?? '—'}</div>
        <div class="sub">${speed.toFixed(0)} m/s</div>
        <button data-cmd="stop">Stop</button>`;
      this.bindButtons(this.shipEl);
    } else {
      this.shipEl.innerHTML = '';
    }

    // Overview
    if (!me) {
      this.overviewEl.innerHTML = docked !== null ? '<div class="sub">In hangar</div>' : '';
      return;
    }
    const rows: { id: number; deco: BallDeco; d: number; ball: Ball }[] = [];
    for (const [id, ball] of sim.park.balls) {
      if (id === sim.myBallId) continue;
      const deco = sim.deco.get(id);
      if (!deco || deco.kind === 'sun') continue;
      const d = Math.hypot(
        ball.newPos.x - me.newPos.x,
        ball.newPos.y - me.newPos.y,
        ball.newPos.z - me.newPos.z,
      );
      rows.push({ id, deco, d, ball });
    }
    rows.sort((a, b) => a.d - b.d);
    this.overviewEl.innerHTML = rows
      .slice(0, 40)
      .map(
        (r) => `
      <div class="ovrow ${r.id === this.selected ? 'sel' : ''} k-${r.deco.kind}" data-id="${r.id}">
        <span class="ovname"></span><span class="ovd">${fmtDist(r.d)}</span>
      </div>`,
      )
      .join('');
    const els = this.overviewEl.querySelectorAll<HTMLElement>('.ovrow');
    els.forEach((el, i) => {
      (el.querySelector('.ovname') as HTMLElement).textContent =
        rows[i].deco.name ?? `${rows[i].deco.kind} ${rows[i].id}`;
      el.addEventListener('click', () => this.select(Number(el.dataset.id), sim));
    });

    // Selected panel refresh (distance changes)
    if (this.selected !== null) this.renderSelected(sim);
  }

  select(id: number | null, sim: ClientSim): void {
    this.selected = id;
    if (id === null) {
      this.selPanelEl.classList.add('hidden');
      return;
    }
    this.renderSelected(sim);
  }

  private renderSelected(sim: ClientSim): void {
    const id = this.selected;
    if (id === null) return;
    const ball = sim.park.balls.get(id);
    const deco = sim.deco.get(id);
    const me = sim.myBall();
    if (!ball || !deco || !me) {
      this.selPanelEl.classList.add('hidden');
      this.selected = null;
      return;
    }
    const d = Math.hypot(
      ball.newPos.x - me.newPos.x,
      ball.newPos.y - me.newPos.y,
      ball.newPos.z - me.newPos.z,
    );
    const buttons: string[] = [
      `<button data-cmd="approach">Approach</button>`,
      `<button data-cmd="orbit" data-arg="5000">Orbit 5 km</button>`,
      `<button data-cmd="keepRange" data-arg="10000">Keep 10 km</button>`,
      `<button data-cmd="warpTo">Warp to</button>`,
    ];
    if (['ship', 'npc', 'asteroid'].includes(deco.kind)) {
      const locked = this.lastStatus?.locked === id;
      buttons.unshift(`<button data-cmd="${locked ? 'unlock' : 'lock'}" class="${locked ? '' : 'primary'}">${locked ? 'Unlock' : 'Lock'}</button>`);
    }
    if (deco.kind === 'wreck') buttons.push(`<button data-cmd="loot" class="primary">Loot</button>`);
    if (deco.kind === 'station') buttons.push(`<button data-cmd="dock" class="primary">Dock</button>`);
    if (deco.kind === 'gate') buttons.push(`<button data-cmd="jump" class="primary">Jump</button>`);

    this.selPanelEl.classList.remove('hidden');
    this.selPanelEl.innerHTML = `
      <div class="ptitle"></div>
      <div class="sub">${deco.kind}${deco.faction ? ' · ' + deco.faction : ''} · ${fmtDist(d)}</div>
      <div class="btnrow">${buttons.join('')}</div>`;
    (this.selPanelEl.querySelector('.ptitle') as HTMLElement).textContent =
      deco.name ?? `${deco.kind} ${id}`;
    this.bindButtons(this.selPanelEl);
  }

  updateStatus(st: import('@deepwake/shared').ServerMessage & { t: 'status' } | null): void {
    this.lastStatus = st;
    if (!st) {
      this.combatEl.classList.add('hidden');
      return;
    }
    this.combatEl.classList.remove('hidden');
    const bar = (v: number, max: number, cls: string) =>
      `<div class="bar"><div class="fill ${cls}" style="width:${max > 0 ? Math.max(0, Math.min(100, (v / max) * 100)) : 0}%"></div><span>${Math.round(v)}/${Math.round(max)}</span></div>`;
    const q = st.hitQuality;
    this.combatEl.innerHTML = `
      ${bar(st.shield, st.shieldMax, 'b-shield')}
      ${bar(st.armor, st.armorMax, 'b-armor')}
      ${bar(st.hull, st.hullMax, 'b-hull')}
      ${bar(st.cap, st.capMax, 'b-cap')}
      <div class="modrack">${st.modules
        .map((m, i) => {
          const def = (window as unknown as { __mods?: Record<string, { name: string; cycleS?: number }> }).__mods?.[m.key];
          return `<button class="mod ${m.active ? 'on' : ''} ${m.disabled ? 'burnt' : ''}" data-slot="${i}" title="${m.key}">
            <span class="modk">F${i + 1}</span>${m.key.split('_')[0]}
            <i class="heat" style="height:${m.heat}%"></i></button>`;
        })
        .join('')}</div>
      ${st.locked !== null ? `<div class="sub">lock ${(st.lockProgress * 100).toFixed(0)}%${q !== null ? ` · hit ${(q * 100).toFixed(0)}%` : ''}${st.scrambled ? ' · <b style="color:#ff7a6a">SCRAMBLED</b>' : ''}</div>` : st.scrambled ? '<div class="sub"><b style="color:#ff7a6a">SCRAMBLED</b></div>' : ''}
      ${st.resonance ? `<div class="sub">resonance <input id="restune" type="range" min="0" max="100" value="${Math.round(st.resonance.tune * 100)}"/>
        <span class="restarget" style="left:${Math.round(st.resonance.target * 100)}%"></span></div>` : ''}
      <div class="sub">cargo ${st.cargoUsed}/${st.cargoMax} m³ · ${st.marks.toLocaleString()} Marks</div>`;
    this.combatEl.querySelectorAll<HTMLButtonElement>('button.mod').forEach((b) => {
      b.addEventListener('click', () => {
        const i = Number(b.dataset.slot);
        this.cb.onModule(i, !st.modules[i].active);
      });
    });
    const slider = this.combatEl.querySelector<HTMLInputElement>('#restune');
    if (slider) slider.addEventListener('input', () => this.cb.onTune(Number(slider.value) / 100));
  }

  private bindButtons(scope: HTMLElement): void {
    scope.querySelectorAll<HTMLButtonElement>('button[data-cmd]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.cb.onCommand(btn.dataset.cmd!, this.selected, btn.dataset.arg ? Number(btn.dataset.arg) : undefined);
      });
    });
  }
}
